import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events, UploadsSortGA4Type} from '@Libraries/ga-events';
import {getLocalStorageItem, setLocalStorageItem} from '@Utils/local-storage.util';
import {isEnumValue} from '@Utils/enum.util';
import type {DisplayedUploadsSortOrderParams, UploadsItemsSortOrderParams, UploadsSortPick, UploadsSortVisibilityParams} from '@Panels/user-media-panel/user-media-panel.types';
import {UploadsMediaSubTab, UploadsMediaType, UploadsSortOrder} from '@Panels/user-media-panel/user-media-panel.types';

const UPLOADS_SORT_ORDER_STORAGE_KEY = 'pmwUploadsSortOrder';

const UPLOADS_SORT_GA4_TYPES: Record<UploadsSortOrder, UploadsSortGA4Type> = {
  [UploadsSortOrder.RECENTLY_USED]: UploadsSortGA4Type.RECENT,
  [UploadsSortOrder.NEWEST_FIRST]: UploadsSortGA4Type.NEWEST,
  [UploadsSortOrder.OLDEST_FIRST]: UploadsSortGA4Type.OLDEST,
  [UploadsSortOrder.ALPHABETICAL_A_TO_Z]: UploadsSortGA4Type.A_TO_Z,
  [UploadsSortOrder.ALPHABETICAL_Z_TO_A]: UploadsSortGA4Type.Z_TO_A,
};

/** Reports a sort the user made. Picking the order the dropdown already shows sorts nothing, so it is not reported. */
export const trackUploadsSortPick = ({displayedSortOrder, pickedSortOrder}: UploadsSortPick): void => {
  if (pickedSortOrder === displayedSortOrder) {
    return;
  }
  trackPosterBuilderGA4Events(GA4EventName.UPLOADS_MODAL_SORT, {[GA4EventParamName.TYPE]: UPLOADS_SORT_GA4_TYPES[pickedSortOrder]});
};

export const isAlphabeticalUploadsSortOrder = (sortOrder: UploadsSortOrder): boolean => {
  return sortOrder === UploadsSortOrder.ALPHABETICAL_A_TO_Z || sortOrder === UploadsSortOrder.ALPHABETICAL_Z_TO_A;
};

/** Only audio items carry a name; image and video items have nothing to sort alphabetically by. */
const doItemsSortAlphabetically = (activeTab: UploadsMediaType): boolean => {
  return activeTab === UploadsMediaType.AUDIO;
};

/**
 * The order the items grid follows. Alphabetical only means something for items that carry a name,
 * i.e. audio; on the image and video tabs it reorders the folders alone while the items keep the
 * last order that applied to them, so picking A-Z neither reloads nor reshuffles unnamed uploads.
 */
export const getUploadsItemsSortOrder = ({sortOrder, lastNonAlphabeticalSortOrder, activeTab}: UploadsItemsSortOrderParams): UploadsSortOrder => {
  if (doItemsSortAlphabetically(activeTab) || !isAlphabeticalUploadsSortOrder(sortOrder)) {
    return sortOrder;
  }
  return lastNonAlphabeticalSortOrder;
};

/**
 * Alphabetical sorting is offered only where something can obey it: audio items on every sub-pill, and the
 * folders grid, which shows only on the All sub-pill and only once the user has folders.
 */
const canAnythingSortAlphabetically = ({activeTab, activeSubtab, doesUserHaveFolders}: UploadsSortVisibilityParams): boolean => {
  return doItemsSortAlphabetically(activeTab) || (activeSubtab === UploadsMediaSubTab.ALL && doesUserHaveFolders);
};

/** The sort orders the dropdown offers on the active tab and sub-pill; recency and age orders are always offered. */
export const getVisibleUploadsSortOrders = (visibilityParams: UploadsSortVisibilityParams): UploadsSortOrder[] => {
  const sortOrders = Object.values(UploadsSortOrder);
  if (canAnythingSortAlphabetically(visibilityParams)) {
    return sortOrders;
  }
  return sortOrders.filter((sortOrder) => {
    return !isAlphabeticalUploadsSortOrder(sortOrder);
  });
};

/**
 * The order the dropdown shows as chosen. A stored choice the active tab and sub-pill do not offer (alphabetical
 * where nothing has a name) stays stored for where it applies; meanwhile the dropdown shows the order the items
 * grid actually follows, which is what getUploadsItemsSortOrder sends for it.
 */
export const getDisplayedUploadsSortOrder = (params: DisplayedUploadsSortOrderParams): UploadsSortOrder => {
  if (getVisibleUploadsSortOrders(params).includes(params.sortOrder)) {
    return params.sortOrder;
  }
  return getUploadsItemsSortOrder(params);
};

/**
 * The sort choice is a lasting preference kept in local storage, so it survives closing the modal,
 * reloads and new browser sessions on this device. This runs while the Redux store boots and
 * local storage can throw (privacy modes), so any failure or unknown stored value falls back to
 * the default instead of surfacing.
 */
export const getStoredUploadsSortOrder = (): UploadsSortOrder => {
  const storedSortOrder = getLocalStorageItem(UPLOADS_SORT_ORDER_STORAGE_KEY);
  return isEnumValue(UploadsSortOrder, storedSortOrder) ? storedSortOrder : UploadsSortOrder.NEWEST_FIRST;
};

export const storeUploadsSortOrder = (sortOrder: UploadsSortOrder): void => {
  setLocalStorageItem(UPLOADS_SORT_ORDER_STORAGE_KEY, sortOrder);
};
